import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Star, Camera } from "lucide-react";
import { toast } from "sonner";

/**
 * Shows all photos uploaded by parents inside their reviews for a given school. Each tile has a
 * "Set as Thumbnail" button that patches school.image_url via POST /api/admin/schools/{id}/set-thumbnail.
 * Used inside the admin New/Edit school dialog.
 */
export default function ReviewPhotosPicker({ schoolId, currentImageUrl = "", onThumbnailSet }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    if (!schoolId) return;
    setLoading(true);
    api.get(`/admin/schools/${schoolId}/review-photos`)
      .then((r) => setPhotos(r.data || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [schoolId]); // eslint-disable-line

  const setAsThumb = async (p) => {
    try {
      setBusyId(p.photo_id);
      const r = await api.post(`/admin/schools/${schoolId}/set-thumbnail`, { photo_id: p.photo_id });
      toast.success("Foto ini sekarang jadi thumbnail sekolah");
      onThumbnailSet?.(r.data.image_url);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusyId(null); }
  };

  if (!schoolId) return null;

  return (
    <div className="border-t border-[#EAE7E0] pt-4 mt-2" data-testid="review-photos-picker">
      <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-2 flex items-center gap-1.5">
        <Camera size={11} /> Foto dari review
        {photos.length > 0 && <span className="text-[10px] normal-case tracking-normal opacity-70">— pilih salah satu untuk jadi thumbnail</span>}
      </div>
      {loading ? (
        <div className="text-sm text-[#4B5563] py-3" data-testid="review-photos-loading">Memuat foto…</div>
      ) : photos.length === 0 ? (
        <div className="text-sm text-[#4B5563] py-3 bg-[#F3F1ED] rounded-xl px-3" data-testid="review-photos-empty">Belum ada foto dari review.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((p, i) => {
            const isCurrent = currentImageUrl && p.data_url === currentImageUrl;
            return (
              <div key={p.photo_id} data-testid={`review-photo-tile-${i}`}
                className={"relative rounded-xl overflow-hidden border aspect-[16/9] bg-[#F3F1ED] group " + (isCurrent ? "border-[#1E3A8A] ring-2 ring-[#DBEAFE]" : "border-[#EAE7E0]")}>
                <img src={p.data_url} alt={p.caption || "Foto review"} className="w-full h-full object-cover" />
                {isCurrent && (
                  <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1E3A8A] text-white text-[10px] uppercase tracking-wider font-semibold"><Star size={9} /> Thumbnail</span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                  <div className="text-[10px] text-white/80 truncate">oleh {p.uploader_name || "Anonim"}</div>
                  {p.caption && <div className="text-[10px] text-white/70 line-clamp-1">{p.caption}</div>}
                  {!isCurrent && (
                    <Button size="sm" disabled={busyId === p.photo_id} onClick={() => setAsThumb(p)} data-testid={`review-photo-set-thumb-${i}`}
                      className="mt-1.5 h-7 rounded-full text-[11px] bg-[#E07A5F] hover:bg-[#C96D55] text-white">
                      <Star size={11} className="mr-1" /> {busyId === p.photo_id ? "Menerapkan…" : "Jadikan Thumbnail"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
